import { TranslateService } from '@ngx-translate/core';
import { ColumnOrganizerType, ColumnType, Pinned, SortDirection, TableDefinition } from '@vendasta/va-filter2-table';
import { combineLatest } from 'rxjs';
import { SalesOrderActionsCellComponent } from './cells/sales-order-actions-cell.component';
import { BusinessInfoCellComponent } from './cells/business-info-cell.component';
import { DateCellComponent } from './cells/date-cell.component';
import { ExpiryCellComponent } from './cells/expiry-cell.component';
import { FulfillmentCellComponent } from './cells/fulfillment-cell.component';
import { OrderIdCellComponent } from './cells/order-id-cell.component';
import { OrderTagsCellComponent } from './cells/order-tags-cell.component';
import { SalespersonCellComponent } from './cells/salesperson-cell.component';
import { StatusCellComponent } from './cells/status-cell.component';
import { SalesOrdersTableFactoryParams, exportDataFormatModalOption } from './interface';
import { CustomerSalesOrdersTableFilterService } from './sales-orders-table-customer-filter.service';
import { SalesOrdersTableFilterService } from './sales-orders-table-filter.service';

const ORDERS_TABLE_DEF: TableDefinition = {
  id: 'sales-orders',
  columnOrganizerType: ColumnOrganizerType.ADVANCED_ORGANIZER,
  labels: {
    // TODO: Translations for these labels in table component
    noResults: 'No results',
    cancel: 'Cancel',
    none: 'None',
    update: 'Update',
    sortBy: 'Sort By',
    thenSortBy: 'Then Sort By',
    addAnotherSortColumn: 'Add another column to sort on',
    chooseColumnToSortBy: 'Choose a column to sort on',
    columnSorting: {
      date: {
        ascending: 'Earliest to Latest',
        descending: 'Latest to Earliest',
      },
      number: {
        ascending: '1 to 9',
        descending: '9 to 1',
      },
      string: {
        ascending: 'A to Z',
        descending: 'Z to A',
      },
      default: {
        ascending: 'ascending',
        descending: 'descending',
      },
    },
    export: {
      buttonText: '',
      buttonTooltipBeforeCount: '',
      buttonTooltipAfterCount: '',
    },
    columnSelector: {
      buttonText: '',
    },
  },
  options: {
    disableMultiColumnSorting: true,
    tableTitle: 'FRONTEND.SALES_UI.ORDER_FILTERS',
    csvExportEnabled: true,
  },
  columns: [
    {
      id: 'orderId',
      displayName: 'FRONTEND.SALES_UI.ORDER_ID',
      pinned: Pinned.PINNED_LEFT,
      type: ColumnType.COLUMN_TYPE_STRING,
      field: 'orderId',
      cellComponent: OrderIdCellComponent,
    },
    {
      id: 'account',
      displayName: 'FRONTEND.SALES_UI.ACCOUNT',
      type: ColumnType.COLUMN_TYPE_STRING,
      field: 'business',
      cellComponent: BusinessInfoCellComponent,
    },
    {
      id: 'status',
      displayName: 'FRONTEND.SALES_UI.STATUS',
      field: 'status',
      cellComponent: StatusCellComponent,
      sortOptions: {
        sortable: true,
      },
    },
    {
      id: 'fulfillmentStatus',
      displayName: 'FRONTEND.SALES_UI.FULFILLMENT',
      field: 'fulfillmentStatus',
      cellComponent: FulfillmentCellComponent,
    },
    {
      id: 'contract-start',
      displayName: 'FRONTEND.SALES_UI.CONTRACT_START',
      type: ColumnType.COLUMN_TYPE_DATE,
      field: 'requestedActivation',
      cellComponent: DateCellComponent,
      sortOptions: {
        sortable: true,
      },
    },
    {
      id: 'salesperson',
      displayName: 'FRONTEND.SALES_UI.SALESPERSON',
      type: ColumnType.COLUMN_TYPE_STRING,
      field: 'salespersonId',
      cellComponent: SalespersonCellComponent,
    },
    {
      id: 'submitted',
      displayName: 'FRONTEND.SALES_UI.SUBMITTED',
      type: ColumnType.COLUMN_TYPE_DATE,
      field: 'created',
      cellComponent: DateCellComponent,
      sortOptions: {
        sortable: true,
        initialSortDirection: SortDirection.SORT_DIRECTION_DESCENDING,
      },
    },
    {
      id: 'expiry',
      displayName: 'FRONTEND.SALES_UI.EXPIRY',
      type: ColumnType.COLUMN_TYPE_STRING,
      translateCell: true,
      field: 'expiry',
      cellComponent: ExpiryCellComponent,
      sortOptions: {
        sortable: true,
      },
    },
    {
      id: 'tags',
      displayName: 'FRONTEND.SALES_UI.TAGS',
      field: 'tags',
      cellComponent: OrderTagsCellComponent,
    },
  ],
};

const salesOrdersActionsCell = {
  id: 'adminActions',
  displayName: 'FRONTEND.SALES_UI.ACTIONS',
  field: '',
  pinned: Pinned.PINNED_RIGHT,
  cellComponent: SalesOrderActionsCellComponent,
};

export const tableDefinitionFactory = (
  filterService: SalesOrdersTableFilterService,
  customerFilterService: CustomerSalesOrdersTableFilterService,
  params: SalesOrdersTableFactoryParams,
  translateService: TranslateService,
) => {
  const tableDefinition = ORDERS_TABLE_DEF;
  if (params) {
    if (params.showActions) {
      if (!tableDefinition.columns.some((c) => c.id === 'adminActions')) {
        tableDefinition.columns.push(salesOrdersActionsCell);
      }
    }
  }

  if (params && params.inBusinessCenter) {
    const removeIds = ['tags', 'salesperson', 'account'];
    tableDefinition.columns = tableDefinition.columns.filter((column) => {
      return !removeIds.includes(column.id);
    });

    tableDefinition.filters = customerFilterService.filters;
    return tableDefinition;
  }

  tableDefinition.filters = filterService.filters;
  tableDefinition.options.csvExportEnabled = true;
  tableDefinition.options.exportDialogDefinition = buildExportModalContent(translateService);
  return tableDefinition;
};

function buildExportModalContent(translateService: TranslateService): any {
  const content = {
    headerText: '',
    descriptionText: '',
    exportOptions: [],
  };

  combineLatest([
    translateService.stream('FRONTEND.SALES_UI.EXPORT.EXPORT_HEADER'),
    translateService.stream('FRONTEND.SALES_UI.EXPORT.EXPORT_DESCRIPTION'),
    translateService.stream('FRONTEND.SALES_UI.EXPORT.ALL_ORDER_OPTION_LABEL'),
    translateService.stream('FRONTEND.SALES_UI.EXPORT.ALL_ORDER_OPTION_DESCRIPTION'),
    translateService.stream('FRONTEND.SALES_UI.EXPORT.ALL_ORDER_WITH_PRODUCT_DATA_OPTION_LABEL'),
    translateService.stream('FRONTEND.SALES_UI.EXPORT.ALL_ORDER_WITH_PRODUCT_DATA_OPTION_DESCRIPTION'),
  ]).subscribe(
    ([
      exportHeaderMessage,
      exportDescriptionMessage,
      orderPerRowOptionText,
      orderPerRowOptionHelpText,
      productsPerRowOptionText,
      productsPerRowOptionHelpText,
    ]) => {
      content.headerText = exportHeaderMessage;
      content.descriptionText = exportDescriptionMessage;
      content.exportOptions = [
        {
          id: exportDataFormatModalOption.ORDERS_PER_ROW,
          selectionText: orderPerRowOptionText,
          selectionHelpText: orderPerRowOptionHelpText,
        },
        {
          id: exportDataFormatModalOption.PRODUCTS_PER_ROW,
          selectionText: productsPerRowOptionText,
          selectionHelpText: productsPerRowOptionHelpText,
        },
      ];
    },
  );

  return content;
}
